import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UserDto } from '@cpm/shared';
import { KeyRound } from 'lucide-react';
import { useState } from 'react';
import { api } from '../api/client.js';
import { useLogout } from '../api/auth.js';
import { Button, Card, ErrorNote, Input, Label } from '../components/ui.js';

/**
 * Full-screen gate shown when the logged-in user carries the
 * must-change-password flag (staff-issued temporary password, G15). The
 * API rejects everything else with 403 PASSWORD_CHANGE_REQUIRED until the
 * change succeeds, so there is deliberately no way around this screen.
 */
export function ForcedPasswordChangePage({ user }: { user: UserDto }) {
  const queryClient = useQueryClient();
  const logout = useLogout();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mismatch, setMismatch] = useState(false);

  const change = useMutation({
    mutationFn: () => api.post('/api/auth/change-password', { currentPassword, newPassword }),
    onSuccess: () => {
      queryClient.setQueryData<UserDto>(['auth', 'me'], { ...user, mustChangePassword: false });
    },
  });

  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-xl bg-brand-600 text-white">
            <KeyRound className="size-6" />
          </div>
          <h1 className="text-lg font-semibold">Choose a new password</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            You signed in with a temporary password. Pick your own before continuing.
          </p>
        </div>
        <Card>
          <form
            className="space-y-4 p-6"
            onSubmit={(e) => {
              e.preventDefault();
              if (newPassword !== confirm) {
                setMismatch(true);
                return;
              }
              setMismatch(false);
              change.mutate();
            }}
          >
            <div>
              <Label htmlFor="current-password">Temporary password</Label>
              <Input
                id="current-password"
                type="password"
                required
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="new-password">New password</Label>
              <Input
                id="new-password"
                type="password"
                required
                minLength={12}
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">At least 12 characters.</p>
            </div>
            <div>
              <Label htmlFor="confirm-password">Repeat new password</Label>
              <Input
                id="confirm-password"
                type="password"
                required
                minLength={12}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
            {mismatch && <ErrorNote error={new Error('Passwords do not match')} />}
            {change.error != null && <ErrorNote error={change.error} />}
            <Button type="submit" className="w-full" loading={change.isPending}>
              Set new password
            </Button>
            <button
              type="button"
              onClick={() => logout.mutate()}
              className="w-full text-center text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400"
            >
              Sign out instead
            </button>
          </form>
        </Card>
      </div>
    </div>
  );
}
